import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import { ScopedSignupModal } from '@components/Modals/ScopedSignupModal.web';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  Invitation,
  TaskPriority,
  useInvitationDetailsQuery,
} from '@graphql/generated';
import { useAuthContext } from '@src/context/authContext';
import theme from '@themes/theme';
import { resolveInvitationSignupPrefill } from '@utils/invitationSignupPrefill';
import { isMobileBrowser } from '@utils/mobileDeepLink';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  [TaskPriority.High]: 'High Priority',
  [TaskPriority.Medium]: 'Medium Priority',
  [TaskPriority.Low]: 'Low Priority',
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  [TaskPriority.High]: theme.colors.alertRed,
  [TaskPriority.Medium]: '#f59e0b',
  [TaskPriority.Low]: theme.colors.blue,
};

const formatDateShort = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/* ─── sub-components ──────────────────────────────────────────────────────── */

const CtaButton: React.FC<{
  label: string;
  onPress: () => void;
  green?: boolean;
}> = ({ label, onPress, green = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole='button'
      style={[
        styles.ctaButton,
        green && styles.ctaButtonGreen,
        hovered && styles.ctaButtonHover,
      ]}>
      <Text style={styles.ctaButtonLabel}>{label}</Text>
    </Pressable>
  );
};

/* ─── main screen ─────────────────────────────────────────────────────────── */

const TaskInvitationPreviewScreenWeb: React.FC = () => {
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const { token: authToken } = useAuthContext();
  const isMobile = isMobileBrowser();

  const invitationToken =
    (typeof window !== 'undefined'
      ? window.sessionStorage.getItem('invitation_token')
      : null) ?? '';

  const taskId =
    (typeof window !== 'undefined'
      ? window.sessionStorage.getItem('task_or_project_id')
      : null) ?? undefined;

  const {
    data,
    loading,
    error: queryError,
  } = useInvitationDetailsQuery({
    variables: {
      token: invitationToken,
      type: Invitation.Task,
      entityId: taskId,
    },
    skip: !invitationToken,
    fetchPolicy: 'network-only',
  });

  const invitationData = data?.invitationDetails ?? null;
  const task = invitationData?.task ?? null;
  const invitedByName = invitationData?.invitedByName ?? '';
  const inviteRole = invitationData?.invitationRole ?? 'Viewer';
  const isLinkInvite = invitationData?.isLinkInvite ?? false;
  // autoJoin=true  → valid non-expired email invite (assigned automatically)
  // autoJoin=false → expired email invite OR shared link invite
  const isAssigned = invitationData?.autoJoin === true;
  // isLinkInvite distinguishes shared links (never expired) from expired email invites
  const isExpired = invitationData != null && !isAssigned && !isLinkInvite;
  // TA-10943: expiry keeps the form in request-to-join mode, but must not blank
  // the invited address — the email lock follows the invitation shape only.
  const signupPrefill = resolveInvitationSignupPrefill(invitationData, {
    isExpired,
  });

  const taskProjectUuid = task?.projectUuid ?? null;
  const taskUsersCount = task?.usersCount ?? null;
  const taskOwnerName = task?.ownerName ?? null;

  useEffect(() => {
    if (taskProjectUuid) {
      window.sessionStorage.setItem(
        'invite_task_project_uuid',
        taskProjectUuid
      );
    }
    if (task?.id) {
      window.sessionStorage.setItem('invite_task_id', task.id);
    }
  }, [taskProjectUuid, task?.id]);

  useEffect(() => {
    if (!task) return;
    window.sessionStorage.setItem('task_invite_name', task.name ?? '');
    window.sessionStorage.setItem('task_invite_inviter_name', invitedByName);
    window.sessionStorage.setItem(
      'task_invite_users_count',
      String(taskUsersCount ?? 0)
    );
    window.sessionStorage.setItem(
      'task_invite_description',
      task.description ?? ''
    );
    window.sessionStorage.setItem('task_invite_priority', task.priority ?? '');
    window.sessionStorage.setItem(
      'task_invite_start_date',
      task.startDate ?? ''
    );
    window.sessionStorage.setItem('task_invite_due_date', task.dueDate ?? '');
    window.sessionStorage.setItem(
      'task_invite_project_name',
      task.projectName ?? ''
    );
    window.sessionStorage.setItem(
      'task_invite_project_icon',
      task.projectIcon ?? ''
    );
  }, [task, invitedByName, taskUsersCount]);

  const handleLogin = () => {
    window.location.href = `${window.location.origin}/log-in`;
  };

  /* ── loading ──────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicatorLoading />
      </View>
    );
  }

  /* ── error / not found ────────────────────────────────────────────────────── */
  if (!task || !!queryError) {
    return (
      <View style={styles.container}>
        <View style={styles.topBar}>
          <Icon name='Logo' width={100} height={28} color='textPrimary' />
        </View>
        <View style={styles.centeredState}>
          <Icon name='AlertTriangle' width={48} height={48} color='grey04' />
          <Text style={styles.errorTitle}>Task Not Found</Text>
          <Text style={styles.errorBody}>
            This invitation link is invalid, expired, or the task no longer
            exists.
          </Text>
          <View style={styles.errorButton}>
            <Button variant='primary' fullWidth onPress={handleLogin}>
              <Text style={styles.errorButtonLabel}>Log in to TaskTag</Text>
            </Button>
          </View>
        </View>
      </View>
    );
  }

  /* ── compute display values ───────────────────────────────────────────────── */
  const priorityLabel = task.priority ? PRIORITY_LABELS[task.priority] : null;
  const priorityColor = task.priority
    ? PRIORITY_COLORS[task.priority]
    : theme.colors.textSecondary;

  const dateRange = (() => {
    if (task.startDate && task.dueDate) {
      return `Due: ${formatDateShort(task.startDate)} – ${formatDateShort(
        task.dueDate
      )}`;
    }
    if (task.dueDate) return `Due: ${formatDateShort(task.dueDate)}`;
    if (task.startDate) return `Start: ${formatDateShort(task.startDate)}`;
    return null;
  })();

  return (
    <View style={styles.container}>
      {/* ── Top bar ───────────────────────────────────────────────────────── */}
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Icon name='Logo' width={100} height={28} color='textPrimary' />
          {authToken ? (
            <View style={styles.topBarAvatar}>
              <Icon name='User' width={18} height={18} color='grey05' />
            </View>
          ) : (
            <Pressable onPress={() => setSignupModalOpen(true)}>
              <Text style={styles.topBarSignUp}>Sign Up</Text>
            </Pressable>
          )}
        </View>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>
        <View style={styles.contentWrapper}>
          {/* ── Header ────────────────────────────────────────────────────── */}
          {isExpired ? (
            <View style={styles.expiredHeader}>
              <View style={styles.expiredIconCircle}>
                <Icon name='Clock' width={32} height={32} color='alertRed' />
              </View>
              <Text
                style={
                  isMobile ? styles.headingTitleMobile : styles.headingTitle
                }>
                Invitation has expired
              </Text>
              <Text style={styles.headingSubtitle}>
                You can still request to join this task.
              </Text>
            </View>
          ) : (
            <View style={styles.assignedHeader}>
              <Text
                style={
                  isMobile ? styles.headingTitleMobile : styles.headingTitle
                }>
                {isAssigned
                  ? "You've been assigned a task"
                  : "You're invited to join a task"}
              </Text>
              {!!invitedByName && !isLinkInvite && (
                <Text style={styles.headingSubtitle}>by {invitedByName}</Text>
              )}
            </View>
          )}

          {/* ── Task card ─────────────────────────────────────────────────── */}
          <View style={styles.taskCard}>
            <View style={styles.taskInfoGroup}>
              <Text style={styles.taskTitle}>{task.name}</Text>
              <View style={styles.taskMetaRow}>
                {!!task.projectName && (
                  <View style={styles.taskMetaItem}>
                    <ProjectAvatar icon={task.projectIcon} />
                    <Text style={styles.taskMetaText}>{task.projectName}</Text>
                  </View>
                )}
                {!!taskOwnerName && (
                  <View style={styles.taskMetaItem}>
                    <View style={styles.assignerAvatar}>
                      <Icon name='User' width={12} height={12} color='white' />
                    </View>
                    <Text style={styles.taskMetaText}>{taskOwnerName}</Text>
                  </View>
                )}
              </View>
            </View>

            {(!!priorityLabel || !!dateRange) && (
              <View style={styles.pillsRow}>
                {!!priorityLabel && (
                  <View style={styles.pill}>
                    <Text style={[styles.pillText, { color: priorityColor }]}>
                      {priorityLabel}
                    </Text>
                  </View>
                )}
                {!!dateRange && (
                  <View style={styles.pill}>
                    <Text style={styles.pillText}>{dateRange}</Text>
                  </View>
                )}
              </View>
            )}

            {!!task.description && (
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Description</Text>
                <Text style={styles.sectionBody}>{task.description}</Text>
              </View>
            )}

            {taskUsersCount != null && taskUsersCount > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Assigned To</Text>
                <View style={styles.taskMetaItem}>
                  <Icon
                    name='Users'
                    width={16}
                    height={16}
                    color='textSecondary'
                  />
                  <Text style={styles.sectionBody}>
                    {taskUsersCount} people working on this task
                  </Text>
                </View>
              </View>
            )}
          </View>

          {/* ── CTA ───────────────────────────────────────────────────────── */}
          <CtaButton
            label={isAssigned ? 'Join This Task' : 'Request to Join'}
            green
            onPress={() => setSignupModalOpen(true)}
          />

          {isExpired && (
            <Text style={styles.adminNote}>
              Admin will receive your request after sign up
            </Text>
          )}

          <Text style={styles.loginRow}>
            {'Already on TaskTag? '}
            <Text onPress={handleLogin} style={styles.loginLink}>
              Log in
            </Text>
          </Text>
        </View>
      </ScrollView>

      {signupModalOpen && (
        <ScopedSignupModal
          invitationType='task'
          entityName={task.name}
          projectName={task.projectName ?? undefined}
          projectIcon={task.projectIcon ?? undefined}
          entityId={taskId}
          isLinkInvite={signupPrefill.isLinkInvite}
          isEmailLocked={signupPrefill.isEmailLocked}
          invitationToken={invitationToken}
          role={inviteRole}
          inviterName={invitedByName}
          email={signupPrefill.email}
          usersCount={taskUsersCount}
          onClose={() => setSignupModalOpen(false)}
          onSuccess={() => setSignupModalOpen(false)}
        />
      )}
    </View>
  );
};

/* ─── styles ──────────────────────────────────────────────────────────────── */

/* eslint-disable design-system/no-component-duplication */
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.grey01,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.grey01,
  },
  centeredState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.l,
    paddingVertical: theme.spacing.xxl,
    maxWidth: 480,
    alignSelf: 'center' as any,
  },

  // ── Top bar ──
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
    position: 'sticky' as any,
    top: 0,
    zIndex: 10,
  },
  topBarInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%' as any,
    maxWidth: 640,
  },
  topBarAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.colors.grey02,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topBarSignUp: {
    color: theme.colors.greenSecondary,
    fontWeight: '600',
    fontSize: 14,
  },

  // ── Scroll ──
  scroll: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.l,
  },
  contentWrapper: {
    width: '100%' as any,
    maxWidth: 640,
  },

  // ── Headers ──
  assignedHeader: {
    alignItems: 'center',
    marginBottom: theme.spacing.l,
    gap: theme.spacing.xxs,
  } as any,
  expiredHeader: {
    alignItems: 'center',
    marginBottom: theme.spacing.l,
    gap: theme.spacing.s,
  } as any,
  expiredIconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: theme.colors.alertRedMild,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headingTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  headingTitleMobile: {
    fontSize: 22,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  headingSubtitle: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },

  // ── Task card ──
  taskCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.m,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)' as any,
    padding: theme.spacing.m,
    marginBottom: theme.spacing.l,
    gap: theme.spacing.m,
  } as any,
  taskInfoGroup: {
    backgroundColor: theme.colors.grey01,
    borderRadius: theme.borderRadii.xs,
    padding: theme.spacing.m,
    gap: theme.spacing.s,
  } as any,
  taskTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  taskMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.m,
    flexWrap: 'wrap',
  } as any,
  taskMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  } as any,
  taskMetaText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  assignerAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.colors.greenDark,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Pills ──
  pillsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.s,
    flexWrap: 'wrap',
  } as any,
  pill: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: theme.spacing.m,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.grey02,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },

  // ── Sections (description, assigned to) ──
  section: {
    gap: 6,
  } as any,
  sectionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  sectionBody: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    lineHeight: 20,
  },

  // ── CTA ──
  ctaButton: {
    height: 56,
    borderRadius: 8,
    backgroundColor: theme.colors.textPrimary,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer' as any,
  },
  ctaButtonGreen: {
    backgroundColor: theme.colors.greenSecondary,
  },
  ctaButtonHover: {
    opacity: 0.88,
  },
  ctaButtonLabel: {
    color: theme.colors.white,
    fontWeight: '600',
    fontSize: 16,
  },

  // ── Admin note (expired invite only) ──
  adminNote: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.s,
  },

  // ── Login row ──
  loginRow: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.m,
  },
  loginLink: {
    color: theme.colors.greenSecondary,
    fontWeight: '600',
    fontSize: 13,
  },

  // ── Error state ──
  errorTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginTop: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
  errorBody: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
  },
  errorButton: {
    marginTop: theme.spacing.xl,
    width: '100%' as any,
  },
  errorButtonLabel: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
});
/* eslint-enable design-system/no-component-duplication */

export { TaskInvitationPreviewScreenWeb };
